import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { captureException } from '../sentry';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidMount() {
    // Reset the boundary whenever the user navigates back/forward. The
    // "Go Back" button calls window.history.back(), but a class
    // ErrorBoundary doesn't re-render on route changes and never cleared
    // `hasError` — so the URL changed underneath while the fallback stayed
    // pinned on screen, making "Go Back" look broken. Listening for
    // popstate lets the previous (working) route render again.
    this._onPopState = () => {
      if (this.state.hasError) {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }
    };
    window.addEventListener("popstate", this._onPopState);
  }

  componentWillUnmount() {
    if (this._onPopState) {
      window.removeEventListener("popstate", this._onPopState);
    }
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Filter out expected errors that shouldn't break the UI
    const isNetworkError = error.message?.includes('Failed to fetch') ||
                          error.message?.includes('Network request failed');
    const isStripeError = error.message?.includes('stripe') ||
                         error.name === 'ScriptError';
    const is401Error = error.message?.includes('401') ||
                      error.message?.includes('Unauthorized');

    // Don't show error boundary for these - they're handled elsewhere.
    // Also don't report to Sentry — these are covered by the beforeSend filter
    // in src/sentry.js, but short-circuiting here saves an unnecessary roundtrip.
    if (isNetworkError || isStripeError || is401Error) {
      console.warn('Recoverable error caught:', error.message);
      // Reset error state after a short delay
      setTimeout(() => {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 100);
      return;
    }

    // Real error — show fallback UI AND report to Sentry for investigation.
    // componentStack contains the React tree at the point of failure, which is
    // the most useful breadcrumb for reproducing the bug.
    captureException(error, {
      componentStack: errorInfo?.componentStack,
      source: 'ErrorBoundary',
    });

    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    // Optionally reload the page
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0a0d14] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-[#1a1f2e] border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Something went wrong</h2>
                <p className="text-gray-400 text-sm">We've encountered an unexpected error</p>
              </div>
            </div>

            {/* Reference ID — short hash of the error so when a user
                emails support, Allison can look up the matching Sentry
                event by this code. Doesn't expose the actual error
                message, which previously dumped raw .toString() output
                including stack-trace fragments to the screen. */}
            {this.state.error && (() => {
              // Tiny hash of error message + first stack frame, used purely
              // for cross-referencing with Sentry. Not cryptographic.
              const seed = `${this.state.error.message || this.state.error}-${this.state.errorInfo?.componentStack || ""}`;
              let h = 0;
              for (let i = 0; i < seed.length; i++) {
                h = ((h << 5) - h) + seed.charCodeAt(i);
                h |= 0;
              }
              const ref = Math.abs(h).toString(36).toUpperCase().slice(0, 8);
              return (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                  <p className="text-red-300 text-sm">
                    Reference ID: <span className="font-mono">{ref}</span>
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Include this code if you contact support — it'll help us find what happened.
                  </p>
                </div>
              );
            })()}

            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 px-4 py-2.5 bg-[var(--primary-color)] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Reload Page
              </button>
              <button
                onClick={() => window.history.back()}
                className="flex-1 px-4 py-2.5 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Go Back
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 text-center">
                If this keeps happening, email{" "}
                <a
                  href="mailto:stackedcare@gmail.com?subject=App%20error%20encountered"
                  className="text-cyan-400 hover:underline"
                >
                  stackedcare@gmail.com
                </a>
                {" "}with the reference ID above.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
